/**
 * Application Bootstrap
 *
 * Initializes all domains before React components use them.
 * Called when user is authenticated.
 *
 * @module App/bootstrap
 */

import type { AuthService } from 'types/AuthService';
import { connectApi } from 'utils/api/baas/axiosBase';
import { reportsApi } from '@reports/infrastructure/api/axiosReport';
import { initReportsComposition } from '@reports/composition';
import { onboardingApi, onboardingPayApi, onboardingAdminApi } from '@onboarding/infrastructure/api/axiosOnboarding';
import { initOnboardingComposition } from '@onboarding/composition';
import { registerOnboardingTranslations } from '@onboarding/locales';
import { initSubscriptionsComposition } from '@subscriptions/composition';
import { registerSubscriptionsTranslations } from '@subscriptions/locales';
import { registerReferralTranslations } from '@referral/locales';
import { initPersonnelComposition } from '@personnel/composition';

/**
 * Initializes all domains that depend on authentication.
 * MUST be called AFTER user is authenticated and BEFORE
 * any component that uses domain hooks is rendered.
 *
 * @param authService - Authentication service from useAuth hook
 *
 * @example
 * // In App/index.js
 * const auth = useAuth();
 *
 * useEffect(() => {
 *   if (auth.isAuthenticated) {
 *     bootstrapApp(auth);
 *   }
 * }, [auth.isAuthenticated]);
 */
export const bootstrapApp = (authService: AuthService): void => {
  initReportsComposition(reportsApi, authService);
  initOnboardingComposition(onboardingApi, onboardingPayApi, onboardingAdminApi, authService);
  registerOnboardingTranslations();
  initSubscriptionsComposition(authService);
  registerSubscriptionsTranslations();
  registerReferralTranslations();
  initPersonnelComposition(connectApi);
};
